import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Scale, Gavel, ChevronDown, Plus, Check, MapPin, Bell, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Justice } from "@/components/Justice";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const FALLBACK_TYPES = [
  { key: "custody", label: "Custody / Parenting Time", family: true },
  { key: "divorce", label: "Divorce / Separation", family: true },
  { key: "child_support", label: "Child Support", family: true },
  { key: "family_other", label: "Family Law – Other", family: true },
  { key: "civil", label: "Civil Dispute", family: false },
  { key: "small_claims", label: "Small Claims", family: false },
  { key: "landlord", label: "Landlord / Tenant", family: false },
  { key: "employment", label: "Employment / Workplace", family: false },
  { key: "protective", label: "Protective Order / Safety", family: false },
  { key: "insurance", label: "Insurance / Property Claim", family: false },
  { key: "payment", label: "Payment / Repayment Dispute", family: false },
  { key: "general", label: "General Evidence Record", family: false },
  { key: "other", label: "Other / Custom", family: false },
];

export function MatterSetup({ onDone }) {
  const [types, setTypes] = useState(FALLBACK_TYPES);
  const [type, setType] = useState("");
  const [jurisdictions, setJurisdictions] = useState({ states: [], international: [] });
  const [jurisdiction, setJurisdiction] = useState("");
  const [title, setTitle] = useState("");
  const [hasCase, setHasCase] = useState(false);
  const [caseNumber, setCaseNumber] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [county, setCounty] = useState("");
  const [judge, setJudge] = useState("");
  const [partyA, setPartyA] = useState("");
  const [partyB, setPartyB] = useState("");
  const [objective, setObjective] = useState("");
  const [representation, setRepresentation] = useState("");
  const [objPresets, setObjPresets] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/matter-types").then(({ data }) => data?.length && setTypes(data)).catch((e) => console.error("load matter-types failed", e)); }, []);
  useEffect(() => { api.get("/jurisdictions").then(({ data }) => setJurisdictions(data)).catch((e) => console.error("load jurisdictions failed", e)); }, []);
  useEffect(() => { api.get("/matters/objective").then(({ data }) => setObjPresets(data.presets || {})).catch(() => {}); }, []);

  const isFamilyType = types.find((t) => t.key === type)?.family;
  const suggestedObjective = objPresets[type] || "";
  const notify = async (region) => {
    try { await api.post("/jurisdiction-interest", { region }); toast.success(`We'll let you know when ${region} is supported`); }
    catch { toast.error("Could not save your interest"); }
  };

  const save = async () => {
    if (!type) { toast.message("Please choose a matter type"); return; }
    if (isFamilyType && !jurisdiction) { toast.message("Please select your state"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/matters", { type, jurisdiction, title, has_case: hasCase, case_number: caseNumber, state: stateVal, county, judge, party_a: partyA, party_b: partyB, objective, representation_status: representation });
      toast.success("Matter created");
      onDone?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create matter");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="matter-setup">
      <div>
        <Label className="ek-mono-label">MATTER TYPE</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {types.map((t) => {
            const active = type === t.key;
            return (
              <button key={t.key} onClick={() => setType(t.key)} data-testid={`matter-type-${t.key}`}
                className="text-left px-3 py-2.5 rounded-md border text-sm transition-all flex items-center justify-between gap-2"
                style={{ borderColor: active ? "var(--ek-primary)" : "var(--ek-border)", background: active ? "rgba(46,130,246,0.08)" : "transparent", color: active ? "var(--ek-text)" : "var(--ek-text-secondary)" }}>
                <span className="flex items-center gap-2">{t.label}{t.family && <span className="ek-mono-label" style={{ color: "var(--ek-child)" }}>FAMILY</span>}</span>
                {active && <Check className="h-4 w-4 shrink-0" style={{ color: "var(--ek-primary)" }} />}
              </button>
            );
          })}
        </div>
      </div>

      {isFamilyType && (
        <div data-testid="jurisdiction-picker">
          <Label className="ek-mono-label">STATE / JURISDICTION <span style={{ color: "var(--ek-error)" }}>*</span></Label>
          <p className="text-xs mt-1" style={{ color: "var(--ek-text-muted)" }}>Required for family matters — unlocks your state's Best-Interest Factor Crosswalk.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 max-h-56 overflow-auto pr-1">
            {[...jurisdictions.states, ...jurisdictions.international].map((s) => {
              const active = jurisdiction === s.code;
              return (
                <button key={s.code} type="button" aria-disabled={!s.available}
                  onClick={() => (s.available ? setJurisdiction(s.code) : notify(s.name))}
                  data-testid={`jurisdiction-${s.code}`}
                  className="text-left px-2.5 py-2 rounded-md border text-xs transition-all flex items-center justify-between gap-1"
                  style={{ borderColor: active ? "var(--ek-primary)" : "var(--ek-border)", background: active ? "rgba(46,130,246,0.08)" : "transparent", color: s.available ? (active ? "var(--ek-text)" : "var(--ek-text-secondary)") : "var(--ek-text-muted)", opacity: s.available ? 1 : 0.65, cursor: "pointer" }}>
                  <span className="truncate">{s.name}</span>
                  {s.available ? (active && <Check className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--ek-primary)" }} />)
                    : <span className="ek-mono-label shrink-0 inline-flex items-center gap-0.5" style={{ color: "#E4B34A" }}><Bell className="h-2.5 w-2.5" />SOON</span>}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] mt-2" style={{ color: "var(--ek-text-muted)" }}>Outside the 48 continental states? Tap a “SOON” region to register your interest (Notify Me).</p>
        </div>
      )}

      <div>
        <Label htmlFor="matter-title">Matter name (optional)</Label>
        <Input id="matter-title" data-testid="matter-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-transparent mt-1" placeholder="e.g. Smith v. Smith or My Documentation" />
      </div>

      {type && (
        <div data-testid="matter-objective">
          <Label htmlFor="matter-objective-input">Your objective (optional)</Label>
          <p className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>What are you documenting toward? EvidenceKeeper uses this to build your Objective-Driven Report.</p>
          <Textarea id="matter-objective-input" data-testid="matter-objective-input" value={objective} onChange={(e) => setObjective(e.target.value)} rows={2} className="bg-transparent mt-1" placeholder={suggestedObjective || "e.g. Maintain a consistent, factual record of parenting time and exchanges."} />
          {suggestedObjective && (
            <button type="button" data-testid="matter-objective-preset" onClick={() => setObjective(suggestedObjective)} className="text-xs underline mt-1" style={{ color: "var(--ek-primary)" }}>
              Use suggested objective
            </button>
          )}
        </div>
      )}

      {type && (
        <div data-testid="matter-representation">
          <Label>Representation (optional)</Label>
          <p className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>This adjusts report wording for your situation.</p>
          <div className="flex gap-2 mt-1">
            {[{ v: "pro_se", l: "Pro Se (Self-Represented)" }, { v: "attorney_represented", l: "Attorney-Represented" }].map((o) => (
              <button key={o.v} type="button" data-testid={`matter-rep-${o.v}`} onClick={() => setRepresentation(representation === o.v ? "" : o.v)}
                className="flex-1 rounded-md border px-3 py-2 text-sm transition-colors"
                style={{ borderColor: representation === o.v ? "var(--ek-primary)" : "var(--ek-border)", background: representation === o.v ? "rgba(46,130,246,0.08)" : "transparent", color: "var(--ek-text)" }}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border rounded-md p-4" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium flex items-center gap-2"><Gavel className="h-4 w-4" style={{ color: "var(--ek-primary)" }} /> Is there an existing court case?</div>
            <p className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>Add case details for your reports and consistency checks.</p>
          </div>
          <Switch checked={hasCase} onCheckedChange={setHasCase} data-testid="matter-hascase-switch" />
        </div>
        {hasCase && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div><Label htmlFor="case-number">Case number</Label><Input id="case-number" data-testid="matter-casenumber-input" value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} className="bg-transparent mt-1" placeholder="2026-DR-0142" /></div>
            <div><Label htmlFor="state">State</Label><Input id="state" data-testid="matter-state-input" value={stateVal} onChange={(e) => setStateVal(e.target.value)} className="bg-transparent mt-1" placeholder="Ohio" /></div>
            <div><Label htmlFor="county">County</Label><Input id="county" data-testid="matter-county-input" value={county} onChange={(e) => setCounty(e.target.value)} className="bg-transparent mt-1" placeholder="Franklin" /></div>
            <div><Label htmlFor="judge">Judge</Label><Input id="judge" data-testid="matter-judge-input" value={judge} onChange={(e) => setJudge(e.target.value)} className="bg-transparent mt-1" placeholder="Hon. R. Lee" /></div>
            <div><Label htmlFor="party-a">Party A (optional)</Label><Input id="party-a" data-testid="matter-partya-input" value={partyA} onChange={(e) => setPartyA(e.target.value)} className="bg-transparent mt-1" placeholder="e.g. Jane Doe" /></div>
            <div><Label htmlFor="party-b">Party B (optional)</Label><Input id="party-b" data-testid="matter-partyb-input" value={partyB} onChange={(e) => setPartyB(e.target.value)} className="bg-transparent mt-1" placeholder="e.g. John Doe" /></div>
          </div>
        )}
      </div>

      <Button onClick={save} disabled={busy || !type || (isFamilyType && !jurisdiction)} data-testid="matter-save-btn" className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
        {busy ? "Creating…" : "Create matter"}
      </Button>
    </div>
  );
}

export default function MatterOnboarding({ onDone, onSkip }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--ek-page)" }} data-testid="matter-onboarding">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl">
          <div className="flex items-center gap-4 mb-6">
            <Justice size={64} />
            <div>
              <div className="ek-stamp inline-block mb-1">SET UP YOUR MATTER</div>
              <h1 className="font-head font-bold text-3xl tracking-tight">What are you documenting?</h1>
              <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>Pick your matter type so EvidenceKeeper tailors the tools to your situation.</p>
            </div>
          </div>
          <div className="border rounded-xl p-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
            <MatterSetup onDone={onDone} />
          </div>
          <div className="text-center mt-4">
            <button onClick={onSkip} data-testid="matter-skip-btn" className="text-sm underline transition-colors hover:text-slate-200" style={{ color: "var(--ek-text-muted)" }}>Skip for now — I'll set this up later</button>
          </div>
        </motion.div>
      </div>
      <DisclaimerFooter />
    </div>
  );
}

export function MatterBar({ matters, activeId, onSwitch, onNew }) {
  const [open, setOpen] = useState(false);
  const active = matters.find((m) => m.id === activeId) || matters[0];
  if (!matters.length) {
    return (
      <button onClick={onNew} data-testid="matter-bar-new" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text-secondary)" }}>
        <Plus className="h-3.5 w-3.5" /> Set up case
      </button>
    );
  }
  return (
    <div className="relative" data-testid="matter-bar">
      <button onClick={() => setOpen((o) => !o)} data-testid="matter-bar-toggle" className="flex items-center gap-2 text-xs px-2.5 sm:px-3 py-1.5 rounded-md border transition-colors max-w-[150px] sm:max-w-[220px]" style={{ borderColor: "var(--ek-primary)", background: "rgba(46,130,246,0.08)", color: "var(--ek-text)" }}>
        <Scale className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--ek-primary)" }} />
        <span className="ek-mono-label hidden sm:inline" style={{ color: "var(--ek-primary)" }}>CHANGE CASE</span>
        <span className="truncate max-w-[80px] sm:max-w-[120px]">{active?.title || "Case"}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-64 rounded-md border z-50 overflow-hidden" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
            <div className="max-h-64 overflow-auto">
              {matters.map((m) => (
                <button key={m.id} onClick={() => { onSwitch(m.id); setOpen(false); }} data-testid={`matter-switch-${m.id}`}
                  className="w-full text-left px-3 py-2.5 text-sm transition-colors hover:bg-white/[0.03] flex items-center justify-between gap-2"
                  style={{ color: m.id === activeId ? "var(--ek-text)" : "var(--ek-text-secondary)" }}>
                  <span className="min-w-0">
                    <span className="block truncate">{m.title}</span>
                    <span className="ek-mono-label">{m.type_label}{m.is_family ? " · FAMILY" : ""}</span>
                  </span>
                  {m.id === activeId && <Check className="h-4 w-4 shrink-0" style={{ color: "var(--ek-primary)" }} />}
                </button>
              ))}
            </div>
            <button onClick={() => { onNew(); setOpen(false); }} data-testid="matter-new-btn" className="w-full text-left px-3 py-2.5 text-sm border-t flex items-center gap-2 transition-colors hover:bg-white/[0.03]" style={{ borderColor: "var(--ek-border)", color: "var(--ek-primary)" }}>
              <Plus className="h-4 w-4" /> New matter
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function NewMatterDialog({ open, onOpenChange, onDone }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-auto" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }}>
        <DialogHeader>
          <DialogTitle className="font-head font-medium tracking-tight">New matter</DialogTitle>
          <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Create another matter and switch between them anytime.</DialogDescription>
        </DialogHeader>
        <MatterSetup onDone={(m) => { onOpenChange(false); onDone?.(m); }} />
      </DialogContent>
    </Dialog>
  );
}

export function StateRequirementsHub({ matter }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && matter?.jurisdiction) {
      setData(null);
      api.get(`/state-statutes/${matter.jurisdiction}`).then(({ data }) => setData(data)).catch(() => setData(null));
    }
  }, [open, matter]);

  if (!matter?.state_requirements_enabled) return null;

  const runCrosswalk = async () => {
    if (!text.trim()) return;
    setBusy(true); setMatches(null);
    try { const { data: r } = await api.post("/crosswalk/suggest", { state: matter.jurisdiction, text }); setMatches(r.matches); }
    catch (e) { toast.error(e.response?.data?.detail || "Justice is unavailable"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="state-hub-btn" className="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border transition-colors" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text-secondary)" }}>
        <MapPin className="h-3.5 w-3.5" style={{ color: "var(--ek-primary)" }} /> {matter.jurisdiction_name} Hub
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-auto" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} data-testid="state-hub-dialog">
          <DialogHeader>
            <DialogTitle className="font-head font-medium tracking-tight flex items-center gap-2"><MapPin className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> State Requirements Hub · {data?.state_name || matter.jurisdiction_name}</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>Custody Factor Crosswalk — best-interest factors courts in this state commonly weigh.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5" data-testid="state-factors">
              {(data?.factors || []).map((f, i) => (
                <div key={i} className="text-sm flex gap-2"><span className="ek-mono-label mt-0.5">{i + 1}</span><span style={{ color: "var(--ek-text-secondary)" }}>{f}</span></div>
              ))}
            </div>
            <div className="border-t pt-3" style={{ borderColor: "var(--ek-border)" }}>
              <Label className="ek-mono-label">CROSSWALK A RECORD</Label>
              <Textarea data-testid="crosswalk-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} className="bg-transparent mt-1" placeholder="Paste a factual record and Justice will suggest which of this state's factors it relates to." />
              <Button onClick={runCrosswalk} disabled={busy || !text.trim()} data-testid="crosswalk-run-btn" className="rounded-md mt-2" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing…</> : "Suggest relevant factors"}
              </Button>
              {matches && (
                <div className="mt-3 space-y-2" data-testid="crosswalk-results">
                  {matches.length === 0 && <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>No clearly relevant factors identified.</p>}
                  {matches.map((m, i) => (
                    <div key={i} className="border rounded-md p-2.5" style={{ borderColor: "rgba(46,130,246,0.3)", background: "rgba(46,130,246,0.05)" }}>
                      <div className="text-sm font-medium">{m.factor}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--ek-text-secondary)" }}>{m.why}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[11px]" style={{ color: "var(--ek-text-muted)" }}>{data?.note} Justice highlights relevance only — it does not determine that any legal standard was met.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
